import type { CSSProperties, PropsWithChildren } from 'react'
import { List, fromJS } from 'immutable'
import type { RenderPhotoProps, RowsPhotoAlbumProps } from 'react-photo-album'
import { RowsPhotoAlbum } from 'react-photo-album'
import { useCallback, useMemo, useRef, useState } from 'react'
import cc from 'classcat'
import loadable from '@loadable/component'

import type { CustomRenderPhotoContext, ExtendedPhoto, GalleryProps, LinkProps, PhotoProps } from './types'
import { useTranslate } from '../../../../../utils/translate'
import Carousel from './Carousel'
import LazyImage from '../../../../LazyImage'
import Lightbox from '../../../Lightbox'
import Portal from '../../../Portal'

const SettingsLayer = loadable(() => import(/* webpackChunkName: "editor" */ '../../SettingsLayer'))
const SettingsForm = loadable(() => import(/* webpackChunkName: "editor" */ './ImageGallerySettings'))

export default function ImageGalleryPlugin({
  config,
  data = fromJS({
    images: [],
  }),
  editorMode,
  editorView,
  onEdit,
  onCancel,
  onDataChange,
  onSave,
}: Readonly<WorkspacePluginProps>) {
  const [isBusy, setIsBusy] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const t = useTranslate('interface')

  const isEmpty = data.get('images', List()).size === 0
  const photos: PhotoProps[] = data
    .get('images', List())
    .toJS()
    .sort((a, b) => a.position - b.position)
    .map((image) => ({
      src: config?.imageUrl?.(image.src),
      originalWidth: image.width,
      originalHeight: image.height,
      width: image.width,
      height: image.height,
      alt: image.alt ?? '',
      link: image.link ?? '',
      opentab: image.opentab,
    }))

  function handleCancel() {
    setIsBusy(false)
    onCancel()
  }

  if (editorView) {
    return (
      <div
        className={cc([
          {
            'dali-grid-element-highlighted': editorMode === 'edit',
            'dali-plugin-imagegallery-busy': isBusy,
          },
        ])}
        ref={ref}
      >
        {isBusy && (
          <div className="dali-plugin-imagegallery-busy-indicator" data-testid="imageGalleryBusyIndicator">
            <span />
          </div>
        )}
        {isEmpty ? (
          <div className="dali-grid-element-placeholder">
            <span className="dali-grid-element-placeholder-icon dali-plugin-imagegallery-placeholder-icon" />
            <button className="dali-grid-element-placeholder-button" onClick={() => onEdit()}>
              {t('components.imageGalleryComponent.imageFieldMultiple.addImagesButton.label')}
            </button>
          </div>
        ) : (
          <Gallery photos={photos} editorView />
        )}
        <SettingsLayer
          open={editorMode === 'edit'}
          virtualRef={ref}
          side="top"
          align="center"
          sideOffset={-600}
          onEscapeKeyDown={handleCancel}
          className="dali-settingslayer-imagegallery"
        >
          {({ renderLayout }) => (
            <SettingsForm
              config={config}
              data={data}
              onDataChange={onDataChange}
              onSave={onSave}
              onCancel={handleCancel}
              onBusyStateChange={setIsBusy}
              renderLayout={renderLayout}
            />
          )}
        </SettingsLayer>
      </div>
    )
  }

  return isEmpty ? null : <Gallery photos={photos} />
}

function Gallery({ photos, editorView }: Readonly<GalleryProps>) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [viewerIsOpen, setViewerIsOpen] = useState(false)

  const photosForCarousel = useMemo(() => {
    return photos.filter((photo) => !photo.link)
  }, [photos])

  const photosForGallery: ExtendedPhoto[] = photos.map((photo) => ({
    src: photo.src,
    originalWidth: photo.width,
    originalHeight: photo.height,
    width: photo.width,
    height: photo.height,
    alt: photo.alt,
    href: photo.link,
    opentab: photo.opentab,
  }))

  const openLightbox: RowsPhotoAlbumProps['onClick'] = useCallback(
    ({ photo }) => {
      if (editorView || photo.href) return

      const indexInCarousel = photosForCarousel.findIndex((p) => p.src === photo.src)

      setCurrentImageIndex(indexInCarousel)
      setViewerIsOpen(true)
    },
    [editorView, photosForCarousel],
  )

  const closeLightbox = () => {
    setCurrentImageIndex(0)
    setViewerIsOpen(false)
  }

  return (
    <div>
      <RowsPhotoAlbum photos={photosForGallery} spacing={4} render={{ photo: renderImage }} onClick={openLightbox} />

      <Portal>
        <Lightbox show={viewerIsOpen} onClose={closeLightbox} fullscreen>
          <Carousel photos={photosForCarousel} initialIndex={currentImageIndex} />
        </Lightbox>
      </Portal>

      <noscript>
        {photos.map((photo, index) => (
          <WrapWithLink link={photo.link} opentab={photo.opentab} key={`${photo.src}${index}`}>
            <img src={`${photo.src}&width=300&height=300`} alt={photo.alt} onError={handleImageError} />
          </WrapWithLink>
        ))}
      </noscript>
    </div>
  )
}

function renderImage(props: RenderPhotoProps, context: CustomRenderPhotoContext) {
  const { onClick } = props
  const { photo, index, width, height } = context
  const style: CSSProperties = { display: 'block', width: width, height: height }

  function handleClick(event) {
    if (onClick) onClick(event)
  }

  return (
    <WrapWithLink link={photo.href} opentab={photo.opentab} key={`${photo.src}${index}`}>
      <LazyImage
        src={photo.src}
        width={photo.originalWidth}
        height={photo.originalHeight}
        alt={photo.alt}
        style={style}
        nofallback
        onClick={handleClick}
        onError={handleImageError}
      />
    </WrapWithLink>
  )
}

function WrapWithLink({ link, opentab, children }: PropsWithChildren<LinkProps>) {
  if (!link) return children

  const target = opentab ? '_blank' : undefined
  const rel = opentab ? 'noopener noreferrer' : undefined

  return (
    <a href={link} target={target} rel={rel}>
      {children}
    </a>
  )
}

function handleImageError(image) {
  if (!image.target.src.includes('&reload=1')) image.target.src = `${image.target.src}&reload=1`
}
